/* eslint-disable no-nested-ternary */
import { Box, BoxProps, InputAdornment, TextField } from '@mui/material';
import { ChangeEvent, useCallback, useMemo } from 'react';
import { useController } from 'react-hook-form';
import { Ingredient } from '../../apiTypes';
import { t } from '../../constants/translations';
import { useAdmin } from '../../contexts/admin.context';
import { useLocale } from '../../contexts/locale.context';
import { getPriceByUnit } from '../../utils/getPriceByUnit';

type FormCostFieldProps = BoxProps & {
  name: string;
  ingredient?: Ingredient;
  qnt: number;
  disabled?: boolean;
};

const FormCostField = ({ name, sx, ingredient, qnt, disabled, ...rest }: FormCostFieldProps) => {
  const { cafe } = useAdmin();
  const { lang } = useLocale();
  const { field } = useController({ name });

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      if (value === '') {
        field.onChange(0);
      }
      const patern = /^\d+(\.\d{0,2})?$/;
      if (patern.test(value)) {
        field.onChange(Number(value) * 100);
      }
    },
    [field]
  );

  const handleChangeCost = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      if (value === '') {
        field.onChange(0);
      }
      const patern = /^\d+(\.\d{0,2})?$/;
      if (patern.test(value)) {
        if (ingredient?.qpp) {
          field.onChange((Number(value) * 100) / (qnt / ingredient.qpp));
          return;
        }
        if (ingredient?.unit === 'g' || ingredient?.unit === 'ml') {
          field.onChange((Number(value) * 100) / (qnt / 1000));
          return;
        }
        field.onChange(Number(field.value) * qnt * 100);
      }
    },
    [field, ingredient?.qpp, ingredient?.unit, qnt]
  );

  const cost = useMemo(() => {
    if (!field.value) {
      return 0;
    }
    return (getPriceByUnit(Number(field.value), ingredient) * qnt) / 100;
  }, [field.value, ingredient, qnt]);

  return (
    <Box sx={{ display: 'flex', gap: 1 }} {...rest}>
      <TextField
        required
        size="small"
        type="number"
        {...field}
        onChange={handleChange}
        value={field.value ? Number(field.value) / 100 : ''}
        fullWidth
        disabled={disabled}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              {cafe?.currency}/
              {ingredient?.qpp
                ? t.pac[lang]
                : `${ingredient?.unit === 'g' ? t.kg[lang] : ingredient?.unit === 'ml' ? t.l[lang] : t.pc[lang]}`}
            </InputAdornment>
          ),
        }}
        sx={{
          div: {
            backgroundColor: (t) => t.palette.background.paper,
          },
        }}
      />
      <TextField
        size="small"
        type="number"
        name={`${name}.total`}
        fullWidth
        onChange={handleChangeCost}
        value={cost}
        disabled={disabled}
        InputProps={{
          endAdornment: <InputAdornment position="end">{cafe?.currency}</InputAdornment>,
        }}
        sx={{
          div: {
            backgroundColor: (t) => t.palette.background.paper,
          },
        }}
      />
    </Box>
  );
};

export default FormCostField;
